'use client';

import { type ReactNode, useActionState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { chromeContent } from '../_content/chrome';
import {
  LIVE_TRANSLATED_KEYS,
  LOCALES,
  type Locale,
  localeFromPath,
  marketingRoutes,
  pathToTranslatedKey,
  route,
} from '../_lib/marketing-routes';
import { subscribeNewsletterFormAction } from '../_lib/server/newsletter-actions';
import { Wordmark } from './wordmark';

const { legal } = marketingRoutes;

const SOCIAL_LINKS: ReadonlyArray<{
  label: string;
  href: string;
  icon: ReactNode;
}> = [
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/locari-ai',
    icon: (
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.34V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.8 0 0 .78 0 1.73v20.53C0 23.22.8 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.74V1.73C24 .78 23.2 0 22.22 0z" />
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61590628529875',
    icon: (
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.03 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/locari.ai',
    icon: (
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 0 1-1.38-.9 3.7 3.7 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63c-.79.3-1.46.72-2.12 1.38C1.36 2.67.94 3.34.63 4.14.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.3.8.72 1.47 1.38 2.13.66.66 1.33 1.08 2.12 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56.8-.3 1.47-.72 2.13-1.38.66-.66 1.08-1.33 1.38-2.13.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91-.3-.8-.72-1.47-1.38-2.13a5.92 5.92 0 0 0-2.13-1.38c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.41-10.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z" />
    ),
  },
];

// Öffentliche UptimeRobot-Statuspage. URL wird eingetragen, sobald der Account
// angelegt ist (Spec docs/superpowers/specs/2026-06-23-observability-und-statuspage).
// Bis dahin leer → der „Systemstatus"-Link wird NICHT gerendert (kein toter Link).
const STATUS_PAGE_URL = '';

const LANG_LABEL: Record<Locale, string> = { de: 'DE', en: 'EN', fr: 'FR' };
const COMING_SOON: Record<Locale, string> = {
  de: 'Bald verfügbar',
  en: 'Coming soon',
  fr: 'Bientôt disponible',
};

/**
 * Marketing site footer – 1:1 port of chrome.js footer (classes/markup identical),
 * locale-aware via usePathname (DE/EN/FR).
 * - Newsletter: aktives DOI-Anmeldeformular (Newsletter-Basis N-3) →
 *   `subscribeNewsletterAction` (Honeypot + Rate-Limit + Double-Opt-in).
 * - LinkedIn: als Text ohne Link (Followup F2).
 * - Sprachumschalter: aktive Sprache = Button; verfügbare Sprachen = Link auf die
 *   Locale-Entsprechung (nur wenn die aktuelle Seite übersetzt ist), sonst disabled.
 * - Nicht übersetzte Ziele (Blog/Karriere/Presse/Changelog/Legal) zeigen auf DE.
 */
export function SiteFooter() {
  const pathname = usePathname();
  const locale = localeFromPath(pathname);
  const c = chromeContent[locale].footer;

  const currentKey = pathToTranslatedKey(pathname);
  // Switcher nur freischalten, wenn die EN/FR-Seite dieses Keys bereits live ist.
  const switchKey =
    currentKey && LIVE_TRANSLATED_KEYS.has(currentKey) ? currentKey : null;
  const topic = (t: string) => `${route('kontakt', locale)}?topic=${t}`;

  return (
    <footer className="footer site-footer" role="contentinfo">
      <div className="container">
        <div className="footer-top">
          <div className="footer-brand">
            <Link
              href={route('home', locale)}
              className="footer-brand-link"
              aria-label={chromeContent[locale].brandAria}
            >
              <Wordmark variant="white" />
            </Link>
            <p>{c.tagline}</p>

            <NewsletterForm locale={locale} />

            <ul className="footer-social" aria-label={c.socialAria}>
              {SOCIAL_LINKS.map((s) => (
                <li key={s.label}>
                  <a
                    href={s.href}
                    rel="noopener noreferrer"
                    target="_blank"
                    aria-label={`Locari · ${s.label}`}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                      focusable="false"
                    >
                      {s.icon}
                    </svg>
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="footer-col">
            <h4>{c.colProduct}</h4>
            <ul>
              <li>
                <Link href={route('service', locale)}>
                  {chromeContent[locale].nav.service}
                </Link>
              </li>
              {/* Use-Case-Index /funktionen ist DE-only (Blog-Präzedenz) →
                  nur in DE zeigen; Slot wie beim Vorgänger direkt nach
                  „Wie es funktioniert" (DP-6). */}
              {locale === 'de' ? (
                <li>
                  <Link href={marketingRoutes.funktionen}>{c.funktionen}</Link>
                </li>
              ) : null}
              <li>
                <Link href={route('ki', locale)}>{c.ki}</Link>
              </li>
              <li>
                <Link href={route('whatsapp', locale)}>{c.whatsapp}</Link>
              </li>
              {/* Mieterselbstauskunft-Generator ist ein kostenloses Produkt-Tool
                  (gehört in den Erklär-Block VOR Sicherheit/Preise), aber
                  DE-Markt-spezifisch (DE-Mietrecht) → nur in DE zeigen.
                  Tool-Label bewusst als DE-Literal (rendert nie in EN/FR –
                  kein chrome.ts-Key nötig, ein Paritäts-Record würde tote
                  EN/FR-Keys erzwingen). */}
              {locale === 'de' ? (
                <li>
                  <Link href={marketingRoutes.mieterselbstauskunft}>
                    Mieterselbstauskunft-Generator
                  </Link>
                </li>
              ) : null}
              <li>
                <Link href={route('sicherheit', locale)}>{c.sicherheit}</Link>
              </li>
              <li>
                <Link href={route('preise', locale)}>
                  {chromeContent[locale].nav.preise}
                </Link>
              </li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>{c.colCompany}</h4>
            <ul>
              <li>
                <Link href={route('ueberUns', locale)}>{c.ueberUns}</Link>
              </li>
              <li>
                <Link href={route('warumLocari', locale)}>
                  {chromeContent[locale].nav.warumLocari}
                </Link>
              </li>
              <li>
                <Link href={route('antiProfiling', locale)}>
                  {chromeContent[locale].nav.antiProfiling}
                </Link>
              </li>
              {/* Blog ist DE-Markt-spezifisch (DE-Mietrecht) → nur in DE zeigen.
                  EN/FR-Blog entsteht später als eigener Markt-Content, nicht als
                  Übersetzung. */}
              {locale === 'de' ? (
                <li>
                  <Link href={marketingRoutes.blog}>{c.blog}</Link>
                </li>
              ) : null}
              <li>
                <Link href={route('changelog', locale)}>{c.changelog}</Link>
              </li>
              <li>
                <Link href={route('karriere', locale)}>{c.karriere}</Link>
              </li>
              <li>
                <Link href={route('presse', locale)}>{c.presse}</Link>
              </li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>{c.colLegal}</h4>
            <ul>
              <li>
                <Link href={legal.hub}>{c.trustCenter}</Link>
              </li>
              <li>
                <Link href={legal.datenschutz}>{c.privacyLabel}</Link>
              </li>
              <li>
                <Link href={legal.cookieRichtlinie}>{c.cookieRichtlinie}</Link>
              </li>
              {/* CookieYes-Revisit: Click öffnet Consent-Banner erneut (Art. 7.3 DSGVO).
                  Als <button> mit der cky-Trigger-Klasse – umgeht die globalen
                  a.cky-banner-element-Overrides (die nur auf <a> zielen). */}
              <li>
                <button type="button" className="cky-banner-element footer-cky">
                  {c.cookieSettings}
                </button>
              </li>
              {/* Einziges übersetztes Legal-Ziel (Freigabe 03.08.2026): in EN/FR
                  auf die jeweilige Sprachfassung, nicht auf die deutsche Seite.
                  In DE liefert route() denselben Pfad wie zuvor `legal.widerruf`. */}
              <li>
                <Link href={route('legalWiderruf', locale)}>{c.widerruf}</Link>
              </li>
              <li>
                <Link href={legal.dsaMeldung}>{c.dsaMeldung}</Link>
              </li>
              <li>
                <Link href={legal.impressum}>{c.impressum}</Link>
              </li>
            </ul>
          </div>

          <div className="footer-col">
            <h4>{c.colContact}</h4>
            <ul>
              <li>
                <Link href={route('kontakt', locale)}>
                  {c.kontaktAufnehmen}
                </Link>
              </li>
              <li>
                <Link href={route('hilfe', locale)}>{c.supportHilfe}</Link>
              </li>
              <li>
                <Link href={route('faq', locale)}>
                  {chromeContent[locale].nav.faq}
                </Link>
              </li>
              {STATUS_PAGE_URL ? (
                <li>
                  <a
                    href={STATUS_PAGE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {c.statusLabel}
                  </a>
                </li>
              ) : null}
              <li>
                <Link href={topic('privacy')}>{c.datenschutzKontakt}</Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="footer-bottom">
          <span>© {c.copyrightSuffix}</span>
          {/* Anschrift auch hier, nicht nur im Impressum — siehe Begruendung an `addressLine`. */}
          <span className="footer-address">{c.addressLine}</span>
          <div className="footer-langs" role="group" aria-label={c.langAria}>
            {LOCALES.map((l) => {
              if (l === locale) {
                return (
                  <button key={l} className="is-active" aria-pressed="true">
                    {LANG_LABEL[l]}
                  </button>
                );
              }
              if (switchKey) {
                return (
                  <Link key={l} href={route(switchKey, l)} hrefLang={l}>
                    {LANG_LABEL[l]}
                  </Link>
                );
              }
              return (
                <button
                  key={l}
                  disabled
                  aria-disabled="true"
                  title={COMING_SOON[l]}
                >
                  {LANG_LABEL[l]}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}

/**
 * Footer-Newsletter-Anmeldung (Newsletter-Basis N-3, §6.2/§6.3). Bewusst NICHT
 * das @kit/ui-`NewsletterSignup` (kein Honeypot, falscher Scope) – ein lokales,
 * `.marketing-scope`-styled Formular mit:
 * - verstecktem Honeypot-Feld `website` (Bots füllen es → stiller Erfolg),
 * - Art.-13-Kurzhinweis (`newsletterNote`) + Link zur Datenschutzerklärung,
 * - neutraler Erfolgsmeldung (kein Enumeration-Orakel).
 * Die Sprache (`locale`) steuert die DOI-Mail-Sprache serverseitig.
 */
function NewsletterForm({ locale }: { locale: Locale }) {
  const c = chromeContent[locale].footer;
  // Progressive Enhancement (23.07.2026): Server-Action direkt am `action`-
  // Attribut statt onSubmit-Handler. Grund: Der onSubmit-Pfad fiel zeitabhängig
  // auf den nativen Browser-Submit zurück (Hydration-Fenster) → stiller
  // Seiten-Reload OHNE Anmeldung (Prod-Vorfall – still verlorene Abonnenten).
  // Mit useActionState + action-Prop funktioniert der Submit auch VOR der
  // Hydration korrekt (voller POST auf die Action, Re-Render mit Erfolgstext).
  const [state, formAction, isPending] = useActionState(
    subscribeNewsletterFormAction,
    { submitted: false },
  );

  return (
    <>
      {state.submitted ? (
        <p className="footer-newsletter-note" role="status">
          {c.newsletterSuccess}
        </p>
      ) : (
        <form
          className="footer-newsletter"
          aria-label={c.newsletterAria}
          action={formAction}
        >
          <label className="sr-only" htmlFor="footer-newsletter-email">
            {c.newsletterEmailLabel}
          </label>
          <input
            id="footer-newsletter-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder={c.newsletterPlaceholder}
            data-test="footer-newsletter-email"
          />
          {/* Sprache der DOI-Mail – als Feld, damit auch der No-JS-POST sie trägt. */}
          <input type="hidden" name="locale" value={locale} />
          {/* Honeypot: für Menschen unsichtbar, von Bots gern ausgefüllt. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            defaultValue=""
            style={{
              position: 'absolute',
              left: '-9999px',
              width: '1px',
              height: '1px',
              opacity: 0,
            }}
          />
          <button
            type="submit"
            className="btn btn-secondary"
            disabled={isPending}
            data-test="footer-newsletter-submit"
          >
            {c.newsletterButton}
          </button>
        </form>
      )}

      {!state.submitted ? (
        <p className="footer-newsletter-note">
          {c.newsletterNote}{' '}
          <Link href={legal.datenschutz}>{c.privacyLabel}</Link>.
        </p>
      ) : null}
    </>
  );
}
